import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Environment } from "@core/config/environment";
import { Path } from "@core/routing/path";
import { QuizWithScore } from "@domain/lms/quiz/quiz";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useIsMobile } from "@utils/hooks";
import { mobile } from "@ui/styles/utils";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { stack, Stack } from "@ui/components/layout";
import { UserView } from "@ui/components/profile/userView";
import { SubAuthor } from "@ui/components/subAuthor";
import { QuizResultIcon } from "./quizResultIcon";

interface QuizResultThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	quiz: QuizWithScore;
}
export const QuizResultThumbnail: React.FC<QuizResultThumbnailProps> = ({ quiz, ...htmlProps }) => {
	const { id, title, userScore: score, author, category } = quiz;
	const isMobile = useIsMobile();
	const displayedAuthor = getDisplayedAuthor(quiz);
	const subAuthor = displayedAuthor !== author ? author : undefined;

	return (
		<Container {...htmlProps}>
			{category && category !== "OTHERS" && <StyledCategoryLabel category={category} />}
			<Stack gap={13} align="center">
				<PrimaryMedium>{title}</PrimaryMedium>
				<QuizResultIcon score={score} />
				<Title>{I18n.t("quiz.shared.score", { score })}</Title>
				<Link to={Path.Quiz(id, isMobile)}>
					<PrimaryButton>
						{I18n.t(quiz.participants.byMe ? "quiz.thumbnail.review" : "quiz.thumbnail.start")}
					</PrimaryButton>
				</Link>
				<StyledUserView
					user={displayedAuthor}
					alignStart={!!subAuthor}
					renderBottomAccessory={subAuthor ? () => <SubAuthor author={subAuthor} displayAuthorAsMe /> : undefined}
				/>
			</Stack>
		</Container>
	);
};

const Container = styled.div`
	background-color: ${({ theme }) =>
		Environment.APP_VARIANT === "jaangal" ? theme.colors.primaryLight : theme.colors.lightBlue};
	border-radius: 12px;
	overflow: hidden;
	text-align: center;
	> * {
		position: relative;
	}
	> img {
		position: absolute;
	}
	position: relative;
	padding: 60px;
	@media ${mobile} {
		padding: 60px 20px 20px;
	}
	${stack(10, "flex-start", "stretch")};
`;

const StyledCategoryLabel = styled(CategoryLabel)`
	position: absolute;
	right: 20px;
	top: 14px;
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
	max-width: 325px;
	word-break: break-word;

	@media ${mobile} {
		max-width: 250px;
	}
`;

const StyledUserView = styled(UserView)`
	margin-top: 10px;
`;
